import { useEffect, useRef } from 'react';

const useAnalytics = (user) => {
  const sessionId = useRef(null);
  const sessionStart = useRef(null);

  useEffect(() => {
    if (user) {
      // Generate session ID
      sessionId.current = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStart.current = Date.now();
      
      // Track app opened
      trackEvent('app_opened', {
        source: 'direct',
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      // Track session end on page unload
      const handleBeforeUnload = () => {
        const duration = Math.floor((Date.now() - sessionStart.current) / 1000);
        trackEvent('session_ended', { duration_seconds: duration });
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        handleBeforeUnload(); // Track session end on component unmount
      };
    }
  }, [user]);

  const trackEvent = async (eventType, eventData = {}) => {
    if (!user || !sessionId.current) return;

    try {
      const token = localStorage.getItem('idToken');
      await fetch('https://cz6psims27vvxgkmdoneze47ge0fnnan.lambda-url.us-east-1.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_type: eventType,
          event_data: eventData,
          session_id: sessionId.current
        })
      });
      console.log('Analytics event tracked:', eventType, eventData);
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  };

  // Specific tracking functions
  const trackPickViewed = (pick) => {
    trackEvent('pick_viewed', {
      pick_id: pick.id,
      profile_id: pick.source?.source_id,
      confidence_level: pick.confidence?.level,
      paywalled: pick.paywalled,
      matchup: pick.matchup
    });
  };

  const trackPickFeedback = (pickId, feedbackType) => {
    trackEvent('pick_feedback', {
      pick_id: pickId,
      feedback: feedbackType
    });
  };

  const trackSubscriptionStarted = (profileId, price) => {
    trackEvent('subscription_started', {
      profile_id: profileId,
      price: price
    });
  };

  const trackSubscriptionCanceled = (profileId, reason = 'user_initiated') => {
    trackEvent('subscription_canceled', {
      profile_id: profileId,
      reason: reason
    });
  };

  const trackPaywallHit = (profileId, converted = false) => {
    trackEvent('paywall_hit', {
      profile_id: profileId,
      converted: converted
    });
  };

  const trackPickAddedToSlip = (pickId) => {
    trackEvent('pick_added_to_slip', {
      pick_id: pickId
    });
  };

  const trackPageView = (pageName) => {
    trackEvent('page_view', {
      page: pageName,
      timestamp: new Date().toISOString()
    });
  };

  return {
    trackEvent,
    trackPickViewed,
    trackPickFeedback,
    trackSubscriptionStarted,
    trackSubscriptionCanceled,
    trackPaywallHit,
    trackPickAddedToSlip,
    trackPageView
  };
};

export default useAnalytics;
